import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { AgendaSettings } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { Field, settingsInputCls } from "@/components/settings/primitives";

const DIAS = [
  { idx: 1, label: "Seg" },
  { idx: 2, label: "Ter" },
  { idx: 3, label: "Qua" },
  { idx: 4, label: "Qui" },
  { idx: 5, label: "Sex" },
  { idx: 6, label: "Sáb" },
  { idx: 0, label: "Dom" },
];

type Expediente = {
  startHour: string;
  endHour: string;
  workingDays: number[];
  lunchBreak: { start: string; end: string } | null;
};

const doSettings = (s: AgendaSettings): Expediente => ({
  startHour: s.startHour,
  endHour: s.endHour,
  workingDays: s.workingDays,
  lunchBreak: s.lunchBreak,
});

const iguais = (a: Expediente, b: Expediente) =>
  a.startHour === b.startHour &&
  a.endHour === b.endHour &&
  [...a.workingDays].sort().join() === [...b.workingDays].sort().join() &&
  (a.lunchBreak?.start ?? null) === (b.lunchBreak?.start ?? null) &&
  (a.lunchBreak?.end ?? null) === (b.lunchBreak?.end ?? null);

/**
 * Quando a organização está aberta.
 *
 * Morava em Configurações → Agenda, junto do estilo do card e do tamanho do slot, como se fosse
 * preferência de tela. Não é: é fato da organização, do mesmo naipe que endereço e telefone — e
 * desde que a Equipe passou a ler o expediente pra dizer se dá pra contar com alguém agora, ele
 * deixou de pertencer a uma tela só. O que ficou na Agenda é o que de fato é dela: como o grid
 * desenha (slot, fim de semana visível, duração padrão da consulta).
 *
 * Salva por um endpoint próprio (`updateExpediente`), que preserva o resto do JSON: esta tela mostra
 * quatro campos e não pode sobrescrever lembrete e visual com valor velho do cliente.
 */
export function ExpedienteSection({ canManage }: { canManage: boolean }) {
  const utils = trpc.useUtils();
  const settingsQuery = trpc.settings.getAgenda.useQuery();
  const configuradoQuery = trpc.settings.expedienteConfigurado.useQuery();

  const salvo = settingsQuery.data ? doSettings(settingsQuery.data) : null;
  const [valores, setValores] = useState<Expediente | null>(null);

  // O formulário nasce do que está salvo — e só depois que a consulta responde.
  useEffect(() => {
    if (salvo && !valores) setValores(salvo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  const salvar = trpc.settings.updateExpediente.useMutation({
    onSuccess: () => {
      toast.success("Expediente salvo.");
      utils.settings.getAgenda.invalidate();
      utils.settings.expedienteConfigurado.invalidate();
      // A Equipe lê o expediente pra montar a situação de cada profissional.
      utils.team.operacaoDoDia.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar o expediente."),
  });

  if (settingsQuery.isLoading || !valores) {
    return <div className="h-40 animate-pulse rounded-lg bg-zinc-100 dark:bg-muted" />;
  }

  const mudou = !!salvo && !iguais(valores, salvo);
  const nuncaConfigurado = configuradoQuery.data?.configurado === false;
  const alterar = (parcial: Partial<Expediente>) => setValores({ ...valores, ...parcial });

  const alternarDia = (dia: number) => {
    const set = new Set(valores.workingDays);
    if (set.has(dia)) set.delete(dia);
    else set.add(dia);
    alterar({ workingDays: [...set].sort() });
  };

  return (
    // O padding é de cada seção nesta tela — não existe wrapper comum que o forneça (ver
    // SettingsClinic). Mesmo `px-6 py-5` da seção "Dados da organização", pra as duas alinharem
    // quando se alterna entre elas.
    <div className="space-y-6 px-6 py-5">
      {nuncaConfigurado && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <p className="font-semibold">Expediente ainda não configurado</p>
          <p className="mt-0.5 text-amber-800 dark:text-amber-300/90">
            Enquanto ninguém definir, a Equipe não consegue dizer se um profissional sem consultas
            está fora do horário ou apenas com a agenda vazia — e prefere avisar a inventar. A
            Agenda desenha a grade num intervalo padrão até você salvar aqui.
          </p>
        </div>
      )}

      {/* Cada campo ocupa a largura do que ele guarda, não a largura do cartão: hora é hora, e um
          input de 07:00 esticado por 430px lê como campo de texto livre. Grade de 12 colunas, o
          mesmo padrão da seção "Dados da organização" ao lado. */}
      <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-12">
        <div className="sm:col-span-3">
          <Field label="Abre às">
            <input
              type="time"
              className={cn(settingsInputCls, "max-w-[168px]")}
              value={valores.startHour}
              disabled={!canManage}
              onChange={(e) => alterar({ startHour: e.target.value })}
            />
          </Field>
        </div>
        <div className="sm:col-span-3">
          <Field label="Fecha às">
            <input
              type="time"
              className={cn(settingsInputCls, "max-w-[168px]")}
              value={valores.endHour}
              disabled={!canManage}
              onChange={(e) => alterar({ endHour: e.target.value })}
            />
          </Field>
        </div>

        <div className="sm:col-span-6">
          <Field label="Dias de funcionamento">
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Dias de funcionamento">
              {DIAS.map((d) => {
                const ativo = valores.workingDays.includes(d.idx);
                return (
                  <button
                    key={d.idx}
                    type="button"
                    disabled={!canManage}
                    aria-pressed={ativo}
                    onClick={() => alternarDia(d.idx)}
                    className={cn(
                      "h-10 min-w-[46px] rounded-md px-2.5 text-xs font-medium transition-colors disabled:opacity-60",
                      ativo
                        ? "bg-brand-600 text-white hover:bg-brand-700"
                        : "border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-muted-foreground",
                    )}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        <div className="sm:col-span-6">
          <Field label="Intervalo de almoço (opcional)" hint="Aparece bloqueado na grade da Agenda.">
            {valores.lunchBreak ? (
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  className={cn(settingsInputCls, "w-[132px] shrink-0")}
                  value={valores.lunchBreak.start}
                  disabled={!canManage}
                  onChange={(e) => alterar({ lunchBreak: { ...valores.lunchBreak!, start: e.target.value } })}
                />
                <span className="text-sm text-zinc-400 dark:text-muted-foreground">até</span>
                <input
                  type="time"
                  className={cn(settingsInputCls, "w-[132px] shrink-0")}
                  value={valores.lunchBreak.end}
                  disabled={!canManage}
                  onChange={(e) => alterar({ lunchBreak: { ...valores.lunchBreak!, end: e.target.value } })}
                />
                {canManage && (
                  <button
                    type="button"
                    onClick={() => alterar({ lunchBreak: null })}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-red-600 dark:hover:bg-muted"
                    aria-label="Remover intervalo de almoço"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                disabled={!canManage}
                onClick={() => alterar({ lunchBreak: { start: "12:00", end: "13:00" } })}
                className="inline-flex h-10 items-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-3 text-xs text-zinc-600 transition-colors hover:border-brand-400 hover:text-brand-700 disabled:opacity-60 dark:border-border dark:text-muted-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                Adicionar intervalo de almoço
              </button>
            )}
          </Field>
        </div>
      </div>

      {canManage && (
        <div className="flex items-center justify-end gap-3 border-t border-zinc-100 pt-4 dark:border-border">
          {mudou && (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Alterações não salvas
            </span>
          )}
          <button
            type="button"
            disabled={!mudou || salvar.isPending}
            onClick={() => salvar.mutate(valores)}
            className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Salvar expediente
          </button>
        </div>
      )}
    </div>
  );
}
